/**
 * InvoicesTable — sortable invoice table with row actions.
 * Stateless; parent owns filter/sort/customer-drawer state. The premium
 * status pill + action icon + style helpers live in this file too because
 * they're only used here (kept close to consumer for cohesion).
 */
import {
  Eye, Edit, DollarSign, Copy, Mail, Printer, Trash2, ArrowUpDown, Bell, Repeat,
} from 'lucide-react';
import { getDeliveryBadge } from '../../services/deliveryTracking';
import { TINT } from '../ui/IconTile';
import { ActionTile } from '../ui/ActionTile';
import { parseInvoiceDate } from '../../utils/invoiceHelpers';

const thStyle = {
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  padding: '12px 16px',
  borderBottom: '1px solid #e5e7eb',
};

const tdStyle = {
  padding: '14px 16px',
  fontSize: 14,
  color: '#1f2937',
  verticalAlign: 'middle',
};

const sortBtnStyle = {
  all: 'unset',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  color: '#64748b',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  fontSize: 11,
};

function PremiumStatusPill({ status }) {
  const map = {
    paid:    { c: '#16a34a', bg: 'rgba(22,163,74,0.10)',  ring: 'rgba(22,163,74,0.20)', label: 'Paid',    glow: true  },
    pending: { c: '#0073ea', bg: 'rgba(0,115,234,0.10)',  ring: 'rgba(0,115,234,0.20)', label: 'Pending', glow: false },
    partial: { c: '#f59e0b', bg: 'rgba(245,158,11,0.12)', ring: 'rgba(245,158,11,0.20)', label: 'Partial', glow: false },
    overdue: { c: '#dc2626', bg: 'rgba(220,38,38,0.10)',  ring: 'rgba(220,38,38,0.22)', label: 'Overdue', glow: true  },
    draft:   { c: '#64748b', bg: 'rgba(100,116,139,0.10)', ring: 'rgba(100,116,139,0.20)', label: 'Draft',  glow: false },
  };
  const s = map[status] || map.draft;
  return (
    <span
      data-testid={`status-pill-${status || 'draft'}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: s.c,
        background: s.bg,
        border: `1px solid ${s.ring}`,
        boxShadow: s.glow ? `0 0 0 3px ${s.ring}33` : 'none',
      }}
    >
      <span
        style={{
          width: 6, height: 6, borderRadius: '50%', background: s.c,
          animation: s.glow ? 'maidly-pulse 2s ease-in-out infinite' : 'none',
        }}
      />
      {s.label}
    </span>
  );
}

/**
 * ActionTile — moved to `components/ui/ActionTile.jsx` so all row action
 * surfaces in the app (Estimates, Recurring, Customers, Employees, Admin
 * panel, etc.) share one design. Imported at the top of this file.
 */

function SortHeader({ label, sortKey, currentKey, dir, onToggle, alignRight }) {
  return (
    <button
      onClick={() => onToggle(sortKey)}
      data-testid={`sort-${sortKey}`}
      style={{
        ...sortBtnStyle,
        ...(alignRight ? { justifyContent: 'flex-end', width: '100%' } : {}),
        color: currentKey === sortKey ? '#0073ea' : '#64748b',
      }}
    >
      {label} <ArrowUpDown size={11} />
      {currentKey === sortKey && (
        <span style={{ fontSize: 9 }}>{dir === 'asc' ? '▲' : '▼'}</span>
      )}
    </button>
  );
}

export default function InvoicesTable({
  invoices,        // filtered+sorted invoices
  sortBy,
  onToggleSort,
  onPreview,
  onEdit,
  onPay,
  onDuplicate,
  onMakeRecurring,
  onSendEmail,
  onSendReminder,
  onPrint,
  onDelete,
  onCustomerClick,
}) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 14,
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
      }}
    >
      <div style={{ overflowX: 'auto' }}>
        <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Invoice #</th>
              <th style={thStyle}>Customer</th>
              <th style={thStyle}>
                <SortHeader
                  label="Date"
                  sortKey="createdAt"
                  currentKey={sortBy.key}
                  dir={sortBy.dir}
                  onToggle={onToggleSort}
                />
              </th>
              <th style={thStyle}>
                <SortHeader
                  label="Due"
                  sortKey="dueDate"
                  currentKey={sortBy.key}
                  dir={sortBy.dir}
                  onToggle={onToggleSort}
                />
              </th>
              <th style={thStyle}>
                <SortHeader
                  label="Amount"
                  sortKey="total"
                  currentKey={sortBy.key}
                  dir={sortBy.dir}
                  onToggle={onToggleSort}
                  alignRight
                />
              </th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td
                  colSpan="7"
                  style={{
                    textAlign: 'center',
                    padding: '2.5rem',
                    color: '#94a3b8',
                    fontSize: 14,
                  }}
                >
                  No invoices match the current filter
                </td>
              </tr>
            ) : (
              invoices.map((invoice) => {
                const totalPaid = (invoice.payments || []).reduce(
                  (sum, p) => sum + Number(p.amount || 0),
                  0,
                );
                const balance = Number(invoice.total || 0) - totalPaid;
                const now = new Date();
                const isOverdue =
                  invoice.status !== 'paid' &&
                  invoice.status !== 'draft' &&
                  invoice.dueDate &&
                  new Date(invoice.dueDate) < now;
                const effectiveStatus = isOverdue ? 'overdue' : invoice.status;
                return (
                  <tr
                    key={invoice.id}
                    data-testid={`invoice-row-${invoice.id}`}
                    style={{
                      borderTop: '1px solid #f1f5f9',
                      transition: 'background 0.15s, transform 0.12s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <PremiumStatusPill status={effectiveStatus} />
                        {(() => {
                          const badge = getDeliveryBadge(invoice.id);
                          if (!badge) return null;
                          return (
                            <span
                              data-testid={`invoice-delivery-badge-${invoice.id}`}
                              title={badge.tooltip}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '2px 8px', fontSize: 11, fontWeight: 600,
                                color: badge.color, background: badge.background,
                                borderRadius: 999, letterSpacing: '0.02em',
                              }}
                            >
                              <span
                                aria-hidden="true"
                                style={{
                                  width: 6, height: 6, borderRadius: '50%',
                                  background: badge.color,
                                }}
                              />
                              {badge.label}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: '#0f172a' }}>
                      {invoice.invoiceNumber}
                    </td>
                    <td style={tdStyle}>
                      <button
                        data-testid={`invoice-customer-${invoice.customerId || invoice.id}`}
                        onClick={() =>
                          invoice.customerId && onCustomerClick(invoice.customerId)
                        }
                        style={{
                          all: 'unset',
                          cursor: invoice.customerId ? 'pointer' : 'default',
                          color: '#0073ea',
                          fontWeight: 500,
                          borderBottom: '1px dashed transparent',
                          transition: 'border-color 0.15s, color 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          if (invoice.customerId)
                            e.currentTarget.style.borderColor = '#0073ea';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'transparent';
                        }}
                      >
                        {invoice.customerName}
                      </button>
                    </td>
                    <td style={{ ...tdStyle, color: '#64748b', fontSize: 13 }}>
                      {parseInvoiceDate(invoice.createdAt)?.toLocaleDateString() || '—'}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        color: isOverdue ? '#dc2626' : '#64748b',
                        fontSize: 13,
                        fontWeight: isOverdue ? 600 : 400,
                      }}
                    >
                      {invoice.dueDate
                        ? parseInvoiceDate(invoice.dueDate)?.toLocaleDateString() || '—'
                        : '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>
                        ${Number(invoice.total || 0).toFixed(2)}
                      </div>
                      {effectiveStatus === 'partial' && (
                        <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 2 }}>
                          ${balance.toFixed(2)} due
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <ActionTile
                          onClick={() => onPreview(invoice)}
                          title="Preview invoice"
                          icon={Eye}
                          tint={TINT.dashboard}
                          testid={`invoice-preview-${invoice.id}`}
                        />
                        <ActionTile
                          onClick={() => onEdit(invoice)}
                          title="Edit invoice"
                          icon={Edit}
                          tint={TINT.timeClock}
                          testid={`invoice-edit-${invoice.id}`}
                        />
                        <ActionTile
                          onClick={() => onPay(invoice)}
                          title={invoice.status === 'paid' ? 'Fully paid' : 'Record payment'}
                          disabled={invoice.status === 'paid'}
                          icon={DollarSign}
                          tint={TINT.invoices}
                          primary
                          label="Pay"
                          testid={`invoice-pay-${invoice.id}`}
                        />
                        <ActionTile
                          onClick={() => onDuplicate(invoice)}
                          title="Duplicate invoice"
                          icon={Copy}
                          tint={TINT.inventory}
                          testid={`invoice-duplicate-${invoice.id}`}
                        />
                        {onMakeRecurring && (
                          <ActionTile
                            onClick={() => onMakeRecurring(invoice)}
                            title="Make recurring — repeat this invoice on a schedule"
                            icon={Repeat}
                            tint={TINT.recurring}
                            testid={`invoice-make-recurring-${invoice.id}`}
                          />
                        )}
                        <ActionTile
                          onClick={() => onSendEmail(invoice)}
                          title="Email to customer"
                          icon={Mail}
                          tint={TINT.scheduling}
                          testid={`invoice-email-${invoice.id}`}
                        />
                        {onSendReminder && (
                          <ActionTile
                            onClick={() => onSendReminder(invoice)}
                            title="Send payment reminder"
                            icon={Bell}
                            tint={TINT.mileage}
                            testid={`invoice-reminder-${invoice.id}`}
                          />
                        )}
                        <ActionTile
                          onClick={() => onPrint(invoice)}
                          title="Print / download PDF"
                          icon={Printer}
                          tint={TINT.settings}
                          testid={`invoice-print-${invoice.id}`}
                        />
                        <ActionTile
                          onClick={() => onDelete(invoice.id)}
                          title="Delete invoice"
                          icon={Trash2}
                          tint={TINT.expenses}
                          testid={`invoice-delete-${invoice.id}`}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
